import React, { useState, useEffect } from 'react';
import { SparklesIcon, LightBulbIcon } from '@heroicons/react/24/outline';
import axios from 'axios';

const VisionRelevanceWidget = ({ categoryId, nodeId, userId }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!userId || !categoryId || !nodeId) return;

    const loadRelevance = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `/api/learning/nodes/${categoryId}/${nodeId}/vision-relevance`,
          { params: { user_id: userId } }
        );
        if (response.data.success) setAnalysis(response.data.analysis);
      } catch (error) {
        console.error('Error loading vision relevance:', error);
      } finally {
        setLoading(false);
      }
    };

    loadRelevance();
  }, [categoryId, nodeId, userId]);

  if (loading) {
    return (
      <div className="aura-card border-t-2 border-t-pine light-surface p-4 mb-6">
        <p className="text-label text-secondary flex items-center gap-2">
          <SparklesIcon className="w-4 h-4" />
          Analyzing relevance to your visions…
        </p>
      </div>
    );
  }

  if (!analysis) return null;

  const relevanceScore = analysis.relevance_score || 5;

  return (
    <div className="aura-card border-t-2 border-t-pine light-surface p-5 mb-6 max-w-prose mx-auto">
      <button
        type="button"
        className="w-full flex items-center justify-between gap-4 text-left"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <div className="flex items-start gap-3">
          <SparklesIcon className="w-5 h-5 text-pine shrink-0 mt-0.5" />
          <div>
            <h4 className="font-display text-h3 text-primary">Relevance to your visions</h4>
            <p className="text-label text-secondary mt-1">
              Score:{' '}
              <span className="figure text-primary tabular-nums">{relevanceScore}/10</span>
            </p>
            <div className="flex gap-1 mt-2">
              {[...Array(10)].map((_, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full border ${
                    i < relevanceScore
                      ? 'bg-pine border-pine'
                      : 'bg-hairline border-hairline'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        <span className="text-label text-faint">{expanded ? '−' : '+'}</span>
      </button>

      {expanded && (
        <div className="mt-4 pt-4 border-t border-hairline space-y-4">
          {analysis.connections && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <LightBulbIcon className="w-4 h-4 text-pine" />
                <h5 className="text-label font-medium text-primary">Key connections</h5>
              </div>
              <p className="text-body text-secondary">{analysis.connections}</p>
            </div>
          )}
          {analysis.insights && (
            <div>
              <h5 className="text-label font-medium text-primary mb-2">Actionable insights</h5>
              <p className="text-body text-secondary">{analysis.insights}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VisionRelevanceWidget;
